import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { fetchPublishedStandaloneBlock } from '../../../../utils/services/complexDocumentsAPI';
import {
  detectAndSplit,
  getHybridBatchList,
  getDocument,
  previewPdf,
  updateHybridBatch,
} from '../../../../utils/services/hybridMailAPI';
import { base64ToPdfBlob } from '../../../../utils/helper';
import { updateEnvelopeAddress } from '../../../../utils/services/hybridMailAPI';
import { measurementUnitConversion } from '../../../../utils/javascript';
import { fetchOrganizationInserts, uploadInsert } from '../../../../utils/services/insertsApi';
import { FontsContext } from '../../../../context/fontsContext';
import { INSERTS_TAB } from '../../../ComplexDocumentDesign/ComplexPdfInserts/translationValues';
import { HYBRID_MAIL } from './TranslationValue';
import { dummyCallback } from '../../../../utils/complexDocumentConstants';

const HybridMailContext = createContext();

export const PDF_SCALE = 1.5;
export const scaleValue = val => val * PDF_SCALE;

export const ENVELOPE_TYPES = {
  DL: {
    x: 120.94488188976379,
    y: 211.65354330708664,
    width: scaleValue(359.05511811023626),
    height: scaleValue(143.62204724409452),
  },
  C5: {
    x: 83.1496062992126,
    y: 226.7716535433071,
    width: scaleValue(359.05511811023626),
    height: scaleValue(143.62204724409452),
  },
  C4: {
    x: 98.26771653543308,
    y: 253.22834645669295,
    width: scaleValue(359.05511811023626),
    height: scaleValue(143.62204724409452),
  },
};

const acceptedExtension = ['pdf'];

export const HybridMailProvider = ({
  children,
  hybridInfo={},
  setHybridInfo,
  onRefresh,
  handleCurrentPage,
  t=dummyCallback,
}) => {
  const { batchName, splitOnCriteria, pageCount, fileName, batchUUID, hybridBatchId, batchInfo,   } = hybridInfo;
  const { fontList } = useContext(FontsContext);
  const batchIdRef = useRef(null);
  const insertsListRef = useRef(null);
  const batchId = batchIdRef.current;
  const [selectedFile, setSelectedFile] = useState(null);
  const [envelopeType, setEnvelopeType] = useState('C5');
  const [shiftSize, setShiftSize] = useState('');
  const [shiftSizePx, setShiftSizePx] = useState(0);
  const [shiftAllVal, setShiftAllVal] = useState(0);
  const [shiftAll, setShiftAll] = useState(false);
  const [shiftUnit, setShiftUnit] = useState('px');
  const [deliveryClass, setDeliveryClass] = useState('First Class');
  const [selectedStationery, setSelectedStationery] = useState(null);
  const [selectedInsert, setSelectedInsert] = useState(null);
  const [stationeryList, setStationeryList] = useState([]);
  const [insertList, setInsertList] = useState([]);
  const [numPages, setNumPages] = useState(null);
  const [addressText, setAddressText] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [dragSize, setDragSize] = useState({ width: 200, height: 80 });
  const [isValidPos, setIsValidPos] = useState(true);
  const [isPreview, setIsPreview] = useState(false);
  const [addressBlocks, setAddressBlocks] = useState([]);
  const [selectedBatchDetailId, setSelectedBatchDetailId] = useState(0);
  const [fontStyle, setFontStyle] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [isSchedulingModalVisible, setIsSchedulingModalVisible] = useState(false);
  const [position, setPosition] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [listLimit, setListLimit] = useState(10);
  const [showRefresh, setShowRefresh] = useState(false);

  //queue states
  const [qSearchValue, setQSearchValue] = useState('');
  const [qSearchByDocName, setQSearchByDocName] = useState(undefined);
  const [qStartAndEndDate, setQStartAndEndDate] = useState({});
  const [qPageFilter, setQPageFilter] = useState('');
  //queue states

  const fileInputRef = useRef(null);
  const documentListRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const itemRefs = useRef([]);
  const debounceTimeoutRef = useRef(null);

  const envelopeTypeChange = useCallback(
    async value => {
      setEnvelopeType(value);
      setAddressBlocks(prev =>
        prev.map(item => ({
          ...item,
          addressBlock: {
            ...item.addressBlock,
            x: null,
            y: null,
          },
        })),
      );
      const env = ENVELOPE_TYPES[value];
      setPosition({ x: env.x, y: env.y + 8});
      await updateHybridBatch({ batchId: batchId, envelopeType: value });
    },
    [batchId],
  );

  const deliveryClassChange = useCallback(
    async value => {
      setDeliveryClass(value);
      await updateHybridBatch({ batchId: batchId, deliveryClass: value });
    },
    [batchId],
  );

  const getAddressById = useCallback(
    (id, direction = 0, updatedList= undefined) => {
      const lettersList = updatedList ?? addressBlocks;
      const currentIndex = lettersList.findIndex(item => item.batchDetailId === id);

      if (currentIndex === -1) {
        return null; // id not found
      }

      // If no direction provided → return exact match
      if (!direction) {
        return lettersList[currentIndex];
      }

      const newIndex = currentIndex + direction;

      // Boundary check
      if (newIndex < 0 || newIndex >= lettersList.length) {
        return null;
      }

      return lettersList[newIndex];
    },
    [addressBlocks],
  );

  const handleStationaryOrPreview = useCallback(
    async (
      isPreview = true,
      isStationery = true,
      isNavigation = false,
      fileName = undefined,
      stationeryValue = selectedStationery,
      insertValue = selectedInsert,
      shiftValue = shiftSizePx,
      batchDetailId = selectedBatchDetailId,
      updatedList = undefined
    ) => {
      if (isPreview) {
        setIsLoading(true);
        setIsPreview(true);
      }
      const env = ENVELOPE_TYPES[envelopeType];
      const addressBlock = getAddressById(batchDetailId, 0, updatedList);
      const fileInfo = addressBlock.fileName ?? addressBlock.splittedFileName;
      const addressInfo = addressBlock.addressBlock;
      delete addressInfo.lineSpacing;
      let insertObj;
      if (insertValue) {
        insertObj = insertsListRef.current.find(insert => insert.id === insertValue);
      }
      // if (!shiftValue) {
      //   shiftValue = shiftAll ? shiftAllVal : shiftSize.get(blockIndex) ?? 0;
      // }
      const json = {
        batchId: Number(batchIdRef.current),
        fileName: fileName ?? fileInfo,
        skipAddress: !isPreview,
        shiftBelowY: shiftValue,
        stationaryId: isStationery ? stationeryValue : null,
        letter:
          isPreview || shiftValue
            ? Object.assign({}, addressInfo, {
                x: (addressInfo?.x ?? env.x),
                y: (addressInfo?.y ?? env.y + 8),
              })
            : undefined,
        ...(insertValue && insertObj && { insert: insertObj }),
      };

      const res = await previewPdf(json);
      if (isNavigation) {
        return res;
      } else {
        setSelectedFile(res.url);
        const addressXY = {
          x: addressBlock?.addressBlock?.x ?? env.x,
          y: addressBlock?.addressBlock?.y ?? env.y + 8,
        };
        setPosition(addressXY);
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      }
    },
    [
      batchId,
      addressBlocks,
      selectedBatchDetailId,
      selectedStationery,
      selectedInsert,
      shiftSize,
      shiftUnit,
      shiftAll,
      shiftAllVal,
      envelopeType,
    ],
  );

  const handleStationery = async value => {
    setSelectedStationery(value);
    setIsLoading(true);
    await updateHybridBatch({ batchId: batchId, stationaryId: value });
    await handleStationaryOrPreview(false, true, false, undefined, value);
  };

  const handleInsert = async value => {
    setSelectedInsert(value);
    setIsLoading(true);
    await updateHybridBatch({ batchId: batchId, insertsId: value });
    await handleStationaryOrPreview(false, true, false, undefined, selectedStationery, value);
  };

  const handleApplyAllShift = e => {
    const checked = e.target.checked;
    setShiftAll(checked);
    const currentValue = shiftSize ?? 0;
    const finalValue = checked ? currentValue : shiftAllVal;
    const pxVal = measurementUnitConversion(value, shiftUnit, 'px');
    if (checked) {
      setShiftAllVal(currentValue);
    }
    setShiftSize(finalValue);
    setShiftSizePx(pxVal);
  };

  const handleShitContent = async val => {
    if (Number.isNaN(Number(val))) return;
    const value = val === '' ? 0 : parseFloat(val);
    if (Number.isNaN(value)) return;
    const pxVal = measurementUnitConversion(value, shiftUnit, 'px');
    setShiftSize(val);
    setShiftSizePx(pxVal);

    setAddressBlocks(prev =>
      prev.map(item =>
        item.batchDetailId === selectedBatchDetailId
          ? {
              ...item,
              shiftContent:pxVal
            }
          : item,
      ),
    );

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      setIsLoading(true);
      await updateHybridBatch({ batchDetailId: selectedBatchDetailId, shiftContent: pxVal });
      handleStationaryOrPreview(
        false,
        true,
        false,
        undefined,
        selectedStationery,
        selectedInsert,
        pxVal,
      );
    }, 1000);
  };

  const handleShiftUnit = async val => {
    const previousUnit = shiftUnit;
    setShiftUnit(val);
    const value = shiftAll ? shiftAllVal : shiftSize ?? 0;
    const updatedValue = measurementUnitConversion(value, previousUnit, val);
    const pxVal = measurementUnitConversion(value, val, 'px');
    if (shiftAll) {
      setShiftAllVal(updatedValue);
    }
    setShiftSize(prev => (prev === '' ? prev : updatedValue));
    setShiftSizePx(pxVal);
    await updateHybridBatch({ batchDetailId: selectedBatchDetailId, unit: val });
  };

  const handleExitPreview = async () => {
    setIsLoading(true);
    await handleStationaryOrPreview(false, true, false);
    setTimeout(() => {
      setIsPreview(false);
    }, 200);
  };

  const setPdfFile = useCallback(
    async (params, batchDetailId, navigationShift, batchInfo= {}) => {
      const {updatedList, stationaryId, insertsId} = batchInfo;
      const shiftSizeVal = navigationShift ?? (shiftAll ? shiftAllVal : shiftSize ?? '0');
      if (selectedStationery || stationaryId || insertsId || isPreview || selectedInsert || shiftSizeVal) {
        const res = await handleStationaryOrPreview(
          isPreview,
          true,
          true,
          params.fileName,
          selectedStationery ?? stationaryId,
          selectedInsert ?? insertsId,
          shiftSizeVal,
          batchDetailId,
          updatedList
        );
        return res.url;
      } else {
        const { base64Content, addressX, addressY } = await getDocument(params);
        const env = ENVELOPE_TYPES[envelopeType];
        const addressXY = { x: addressX ?? env.x, y: addressY ?? env.y + 8 };
        setPosition(addressXY);
        checkValidPosition(addressXY.x, addressXY.y);
        const blob = base64ToPdfBlob(base64Content);
        const url = URL.createObjectURL(blob);
        return url;
      }
    },
    [
      shiftAll,
      shiftAllVal,
      shiftSize,
      selectedBatchDetailId,
      selectedStationery,
      isPreview,
      selectedInsert,
      envelopeType,
    ],
  );

  const detectAddresses = useCallback(
    async batchUUID => {
      setIsLoading(true);
      try {
        const {
          data: { batchId, files, splittingCompleted },
        } = await detectAndSplit({batchUUID});
        batchIdRef.current = batchId;
        // setBatchId(batchId);
        setShowRefresh(splittingCompleted);
        if (batchId && files.length) {
          const pdfUrl = await setPdfFile({
            batchDetailId: files[0].batchDetailId,
            fileName: files[0].fileName,
          });
          setSelectedFile(pdfUrl);
        }
        const blocks = files;
        setAddressBlocks(blocks || []);
        if (blocks?.length) {
          const addressBlock = blocks[0].addressBlock;
          setSelectedBatchDetailId(blocks[0].batchDetailId);
          setAddressText(addressBlock.text);
          const env = ENVELOPE_TYPES[envelopeType];
          const addressXY = { x: addressBlock?.x ?? env.x, y: addressBlock?.y ?? env.y + 8 };
          setPosition(addressXY);
          setFontStyle(blocks[0].addressBlock);
        }
      } catch (error) {
        message.error('Address detection failed');
        console.error('Address detection error:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [batchName, envelopeType, splitOnCriteria, pageCount],
  );

  

  const handleSendLetter = async () => {
    setIsSchedulingModalVisible(true);
  };

  const handleProcessSelectedText = async () => {
    if (!selectedText) return;
    setIsLoading(true);
    try {
      const selectedPdf = addressBlocks.find(item => item.batchDetailId === selectedBatchDetailId);
      const payload = {
        batchDetailId:selectedBatchDetailId,
        correctAddressText: selectedText,
        previousAddressBlock: selectedPdf?.addressBlock,
      };
      const res = await updateEnvelopeAddress(payload);
      if (res.status === 'SUCCESS') {
        setAddressBlocks(prev =>
          prev.map((item, i) =>
            item.batchDetailId === selectedBatchDetailId
              ? {
                  ...item,
                  addressBlock: {
                    ...item.addressBlock,
                    ...res.newAddressBlock,
                    x: item.addressBlock.x,
                    y: item.addressBlock.y,
                  },
                }
              : item,
          ),
        );
        setAddressText(res.newAddressBlock.text);
        const pdfUrl = await setPdfFile({
          batchDetailId: selectedBatchDetailId,
          fileName: res.fileName,
        });
        setSelectedFile(pdfUrl);
      }
    } catch (error) {
      console.error('Error processing text:', error);
    } finally {
      setIsLoading(false);
      setSelectedText('');
      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      }
    }
  };

  const handleNavigation = async (direction, batchDetailId = null) => {
    let currentAddress;
    if (direction === 'click' && batchDetailId) {
      if (selectedBatchDetailId === batchDetailId) return;
      currentAddress = getAddressById(batchDetailId);
      const updatedValue = measurementUnitConversion(currentAddress.shiftContent, 'px', currentAddress.unit??'px');
      setShiftSize(updatedValue);
      setShiftSizePx(currentAddress?.shiftContent ?? 0);
    } else {
      // newIndex = selectedBatchDetailId + direction;
    }

    setIsLoading(true);
    const pdfUrl = await setPdfFile(
      { batchDetailId, fileName: currentAddress.fileName },
      currentAddress.batchDetailId,
      currentAddress?.shiftContent ?? 0

    );
    setTimeout(() => {
      setSelectedBatchDetailId(currentAddress.batchDetailId);
      setSelectedFile(pdfUrl);
      setAddressText(currentAddress.addressBlock.text);
      setFontStyle(currentAddress.addressBlock);
      const env = ENVELOPE_TYPES[envelopeType];
      const addressXY = {
        x: currentAddress.addressBlock?.x ?? env.x,
        y: currentAddress.addressBlock?.y ?? env.y + 8,
      };
      setPosition(addressXY);
      setIsLoading(false);
    }, 500);
  };

  const handleRefreshList = useCallback(
    async (pageNumber = undefined, searchValue = undefined) => {
      try {
        setIsLoading(true);
        const {
          data: { files, splittingCompleted, envelopType: hybridEnvelopeType, stationaryId, insertsId },
          dataCount,
        } = await getHybridBatchList({ hybridBatchId: batchId, page: pageNumber ?? currentPage, text: searchValue });
        const totalData = dataCount;
        const totalPages = Math.ceil(totalData / listLimit);
        setAddressBlocks(files);
        if (files.length && totalData && !isNaN(totalPages)) {
          const pxVal = files[0]?.shiftContent ?? 0;
          const letterUnit = files[0].unit ?? 'px';
          const convertedSize = measurementUnitConversion(pxVal,'px',letterUnit)

          const pdfUrl = await setPdfFile({
            batchDetailId: files[0].batchDetailId,
            fileName: files[0].fileName || files[0].splittedFileName,
          }, files[0].batchDetailId , pxVal, {updatedList: files, stationaryId, insertsId} );
          setEnvelopeType(hybridEnvelopeType);
          setShiftSize(convertedSize);
          setShiftSizePx(pxVal);
          setShiftUnit(letterUnit);
          setSelectedFile(pdfUrl);
          const addressBlock = files[0].addressBlock;
          setSelectedBatchDetailId(files[0].batchDetailId);
          setAddressText(addressBlock.text);
          const env = ENVELOPE_TYPES[hybridEnvelopeType];
          const addressXY = { x: addressBlock?.x ?? env.x, y: addressBlock?.y ?? env.y + 8 };
          setPosition(addressXY);
          setFontStyle(files[0].addressBlock);
          setTotalRecords(totalData);
          setTotalPages(totalPages);
          setShowRefresh(splittingCompleted);
        }
      } catch (error) {
        console.error('Error refreshing list:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [batchId, isPreview],
  );

  const getLetter = useCallback(
    async (info) => {
      const {
        batchId,
        batchDetailId,
        fileName,
        selectedStationery,
        selectedInsert,
        shiftAll,
        shiftAllVal,
        shiftSizePx,
        isPreview,
        envelopeType,
        addressBlock
      } = info;

      const shiftSizeVal = shiftAll ? shiftAllVal : shiftSizePx ?? '0';
      if (selectedStationery || isPreview || selectedInsert || shiftSizeVal) {

      const env = ENVELOPE_TYPES[envelopeType];

      const json = {
        batchId: Number(batchId),
        fileName: fileName ,
        skipAddress: !isPreview,
        shiftBelowY: shiftSizeVal,
        stationaryId: selectedStationery ?? null,
        letter: shiftSizeVal
        ? Object.assign({}, addressBlock, {
            x: (addressBlock?.x ?? env.x) / PDF_SCALE,
            y: (addressBlock?.y ?? env.y + 8) / PDF_SCALE + 8,
          })
        : undefined,
        ...(selectedInsert && { insert: selectedInsert }),
      };

      const res = await previewPdf(json);
    
      setSelectedFile(res.url);
      const addressXY = {
        x: addressBlock?.x ?? env.x,
        y: addressBlock?.y ?? env.y + 8,
      };
      setPosition(addressXY);
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
       
      return res.url;
      } else {
        const { base64Content, addressX, addressY } = await getDocument({batchDetailId,fileName});
        const env = ENVELOPE_TYPES[envelopeType];
        const addressXY = { x: addressX ?? env.x, y: addressY ?? env.y + 8 };
        setPosition(addressXY);
        checkValidPosition(addressXY.x, addressXY.y);
        const blob = base64ToPdfBlob(base64Content);
        const url = URL.createObjectURL(blob);
        return url;
      }
    },
    [],
  );

  const getBatchDetails = useCallback(async batchId => {
    try {
      setIsLoading(true);
      const {
        data: { files, splittingCompleted, deliveryClass, envelopType: hybridEnvelopeType, insertsId, stationaryId, shiftContent, unit },
        dataCount,
      } = await getHybridBatchList({ hybridBatchId: batchId, page: 1 });
      setDeliveryClass(deliveryClass);
      setEnvelopeType(hybridEnvelopeType);
      setSelectedInsert(insertsId);
      setSelectedStationery(stationaryId);
      const pxVal = files[0]?.shiftContent ?? 0;
      const letterUnit = files[0].unit ?? 'px';
      const convertedSize = measurementUnitConversion(pxVal,'px',letterUnit)
      setShiftSize(convertedSize);
      setShiftSizePx(pxVal);
      setShiftUnit(letterUnit);
      const totalData = dataCount;
      const totalPages = Math.ceil(totalData / listLimit);
      
      if (files.length && totalData && !isNaN(totalPages)) {
        
        const insertObj = insertList.find(insert => insert.id === insertsId);
        const pdfUrl = await getLetter({
          batchId,
          batchDetailId: files[0].batchDetailId,
          fileName: files[0].fileName || files[0].splittedFileName,
          selectedStationery: stationaryId,
          selectedInsert: insertObj,
          shiftAll: false,
          shiftAllVal: shiftContent,
          shiftSizePx: pxVal,
          isPreview: false,
          envelopeType: hybridEnvelopeType,
          addressBlock: files[0].addressBlock
        });
        setSelectedFile(pdfUrl);
      }
      const blocks = files;

      setAddressBlocks(blocks || []);
      if (blocks?.length) {
        const addressBlock = blocks[0].addressBlock;
        setSelectedBatchDetailId(blocks[0].batchDetailId);
        setAddressText(addressBlock.text);
        const env = ENVELOPE_TYPES[hybridEnvelopeType];
        const addressXY = { x: addressBlock?.x ?? env.x, y: addressBlock?.y ?? env.y + 8 };
        setPosition(addressXY);
        setFontStyle(blocks[0].addressBlock);
        setTotalRecords(totalData);
        setTotalPages(totalPages);
        setShowRefresh(splittingCompleted);
      }
    } catch (error) {
      console.error('Error refreshing list:', error);
    } finally {
      setIsLoading(false);
    }
  }, [stationeryList, insertList]);

  const handleOnChangePage = (type, value) => {
    if (type === 'input') {
      const num = Number(value);
      if (Number.isNaN(num)) return;

      if (value === '') {
        setCurrentPage('');
        return;
      }

      if (num <= 0) {
        setCurrentPage(value === '0' ? '' : value);
        return;
      }

      if (num < totalPages) {
        setCurrentPage(num);
        handleRefreshList(num);
        return;
      }

      setCurrentPage(prev => (num > totalPages ? prev : value));
    }

    if (type === 'prev') {
      const num = Number(currentPage);
      const updatedPage = num > 1 ? num - 1 : 1;
      setCurrentPage(updatedPage);
      handleRefreshList(updatedPage);
    }

    if (type === 'next') {
      const updatedPage = Number(currentPage) + 1;
      setCurrentPage(updatedPage);
      handleRefreshList(updatedPage);
    }
  };

  const handleInputSearch = useCallback((value) => {
    if (!batchId) return;
    console.log(value);
    handleRefreshList(undefined, value);
  }, [batchId, handleRefreshList]);

  const updateXYByIndex = async (newX, newY) => {
    const scaleDownX= newX / PDF_SCALE;
    const scaleDownY= newY / PDF_SCALE + 8;

    setPosition({ x: scaleDownX, y: scaleDownY });
    setAddressBlocks(prev =>
      prev.map(item =>
        item.batchDetailId === selectedBatchDetailId
          ? {
              ...item,
              addressBlock: {
                ...item.addressBlock,
                x: scaleDownX,
                y: scaleDownY,
              },
            }
          : item,
      ),
    );
    checkValidPosition(scaleDownX, scaleDownY);
    await updateHybridBatch({
      batchDetailId: selectedBatchDetailId,
      addressX: scaleDownX,
      addressY: scaleDownY
    });
  };

  const checkValidPosition = useCallback(
    (x, y) => {
      const env = ENVELOPE_TYPES[envelopeType];
      const { width, height } = dragSize;
      const inside =
        x >= env?.x &&
        y >= env?.y &&
        x + width <= env?.x + env.width &&
        y + height <= env?.y + env.height;

      setIsValidPos(inside);
    },
    [envelopeType, dragSize],
  );

  const updateAddressText = async val => {
    setAddressText(val);
    setAddressBlocks(prev =>
      prev.map(item =>
        item.batchDetailId === selectedBatchDetailId
          ? {
              ...item,
              addressBlock: {
                ...item.addressBlock,
                text: val,
              },
            }
          : item,
      ),
    );

    await updateHybridBatch({ batchDetailId: selectedBatchDetailId, address: val });
  };

  const handleInsertClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async event => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      message.error(
        t(
          INSERTS_TAB.UPLOADED_FILE_EXTENSION_ERROR_MESSAGE.key,
          INSERTS_TAB.UPLOADED_FILE_EXTENSION_ERROR_MESSAGE.value,
          { value: acceptedExtension.join(', ') },
        ),
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    setUploadLoading(true);
    try {
      const response = await uploadInsert(file);
      if (response.statusCode === 125) {
        const insertResponse = await fetchOrganizationInserts();
        if (insertResponse?.statusCode === 129 && insertResponse?.data) {
          const listOfInserts = [{ id: null, name: t(HYBRID_MAIL.NONE_OPTION.key, HYBRID_MAIL.NONE_OPTION.value) }, ...insertResponse.data];
          setInsertList(listOfInserts);
          insertsListRef.current = listOfInserts
        }
        message.success(
          t(INSERTS_TAB.INSERT_UPLOAD_SUCCESS.key, INSERTS_TAB.INSERT_UPLOAD_SUCCESS.value),
        );
      }
    } catch (error) {
      console.error('Upload failed:', error);
      message.error(
        t(INSERTS_TAB.INSERT_UPLOAD_FAILURE.key, INSERTS_TAB.INSERT_UPLOAD_FAILURE.value),
      );
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    if (batchUUID) {
      detectAddresses(batchUUID);
    }
  }, [batchUUID]);

  useEffect(() => {
    const pos = getAddressById(selectedBatchDetailId)?.addressBlock;
    const env = ENVELOPE_TYPES[envelopeType];
    checkValidPosition(pos?.x ?? env.x, pos?.y ?? env.y + 8);
  }, [dragSize, envelopeType, selectedBatchDetailId]);

  useEffect(() => {
    if (!addressText) return;

    const lines = addressText.split('\n');
    setDragSize({
      width: Math.max(180, Math.max(...lines.map(l => l.length)) * 8 + 20),
      height: Math.max(60, lines.length * 20 + 20),
    });
  }, [addressText]);

  // useEffect(() => {
  //   if (selectedBatchDetailId >= 0 && itemRefs.current[selectedBatchDetailId]) {
  //     itemRefs.current[selectedBatchDetailId].scrollIntoView({
  //       behavior: 'smooth',
  //       block: 'nearest',
  //     });
  //   }
  // }, [selectedBatchDetailId]);

  useEffect(() => {
    (async () => {
      const response = await fetchPublishedStandaloneBlock({
        tags: 'Stationary',
      });
      if (response?.statusCode === 2505 && response.data) {
        const listOfStationeries = [
          { blockName: t(HYBRID_MAIL.NONE_OPTION.key, HYBRID_MAIL.NONE_OPTION.value), blockId: null },
          ...response.data,
        ];
        setStationeryList(listOfStationeries);
      }

      const insertResponse = await fetchOrganizationInserts();
      if (insertResponse?.statusCode === 129 && insertResponse?.data) {
        const listOfInserts = [{ id: null, name: t(HYBRID_MAIL.NONE_OPTION.key, HYBRID_MAIL.NONE_OPTION.value) }, ...insertResponse.data];
        setInsertList(listOfInserts);
        insertsListRef.current = listOfInserts
      }
    })();
  }, []);

  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        setSelectedText('');
        return;
      }

      const range = selection.getRangeAt(0);
      const container = pdfContainerRef.current;

      if (container && container.contains(range.commonAncestorContainer)) {
        const text = selection.toString().trim();
        setSelectedText(text);
      } else {
        setSelectedText('');
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  useEffect(() => {
    if (fontList.qlFonts && fontStyle?.fontName) {
      const isFontPresent = fontList.qlFonts.includes(fontStyle.fontName);
      if (!isFontPresent) {
        message.open({
          type: 'warning',
          content: t(HYBRID_MAIL.FONT_NOT_AVAILABLE.key, HYBRID_MAIL.FONT_NOT_AVAILABLE.value, { fontName: fontStyle.fontName }),
          duration: 4,
        });
      }
    }
  }, [fontStyle, fontStyle.fontName, fontList.qlFonts]);

  useEffect(() => {
    if(hybridBatchId && !batchUUID && batchInfo && stationeryList.length>0 && insertList.length >0){
      // setBatchId(hybridBatchId);
      batchIdRef.current = hybridBatchId;
      getBatchDetails(hybridBatchId);
    }
  }, [hybridBatchId, stationeryList, insertList])

  const value = {
    selectedFile,
    envelopeType,
    shiftSize,
    shiftAllVal,
    shiftAll,
    shiftUnit,
    deliveryClass,
    selectedStationery,
    selectedInsert,
    stationeryList,
    insertList,
    numPages,
    batchId,
    addressText,
    uploadLoading,
    dragSize,
    isValidPos,
    isPreview,
    addressBlocks,
    selectedBatchDetailId,
    fontStyle,
    isLoading,
    selectedText,
    isSchedulingModalVisible,
    fileInputRef,
    documentListRef,
    itemRefs,
    position,
    showRefresh,
    setPosition,
    envelopeTypeChange,
    deliveryClassChange,
    handleStationery,
    handleInsert,
    handleApplyAllShift,
    handleShitContent,
    handleShiftUnit,
    handleStationaryOrPreview,
    handleExitPreview,
    detectAddresses,
    handleSendLetter,
    handleProcessSelectedText,
    handleNavigation,
    updateXYByIndex,
    updateAddressText,
    handleInsertClick,
    handleFileChange,
    setIsSchedulingModalVisible,
    onRefresh,
    t,
    setNumPages,
    setIsLoading,
    setShowRefresh,
    handleRefreshList,
    currentPage,
    setCurrentPage,
    totalRecords,
    totalPages,
    handleOnChangePage,
    pdfContainerRef,
    handleCurrentPage,
    handleInputSearch,
    qSearchValue,
    setQSearchValue,
    qSearchByDocName,
    setQSearchByDocName,
    qStartAndEndDate,
    setQStartAndEndDate,
    qPageFilter,
    setQPageFilter
  };

  return <HybridMailContext.Provider value={value}>{children}</HybridMailContext.Provider>;
};

export const useHybridMail = () => {
  const context = useContext(HybridMailContext);
  if (!context) {
    throw new Error('useHybridMail must be used within a HybridMailProvider');
  }
  return context;
};
